/**
 * AudioManager - Professional audio system with synthetic sounds
 * No external audio files needed - generates sounds via Web Audio API
 * Handles SFX, Music, and dynamic rolling sounds
 */

import eventBus, { GameEvents } from '../EventBus';
import Phaser from 'phaser';

class AudioManager {
    constructor() {
        this.scene = null;
        this.audioContext = null;
        this.isInitialized = false;
        
        // Volume levels (0-1)
        this.masterVolume = 1.0;
        this.sfxVolume = 0.7;
        this.musicVolume = 0.4;
        
        // Sound buffers
        this.soundBuffers = new Map();
        
        // Rolling sound state
        this.rollingOscillator = null;
        this.rollingGain = null;
        this.rollingSoundPlaying = false;
        
        // Music state
        this.musicOscillators = [];
        this.musicGain = null;
        this.musicPlaying = false;
        
        // Settings
        this.sfxEnabled = true;
        this.musicEnabled = true;
        
        // Load settings
        this.loadSettings();
    }
    
    loadSettings() {
        try {
            // Use consistent key with MenuScene
            const saved = localStorage.getItem('bigblueball_settings');
            if (saved) {
                const settings = JSON.parse(saved);
                this.sfxEnabled = settings.soundEnabled !== false;
                this.musicEnabled = settings.musicEnabled !== false;
            }
        } catch (e) {
            // Use defaults
        }
    }
    
    /**
     * Initialize with a Phaser scene
     */
    init(scene) {
        this.scene = scene;
        
        // Get Web Audio context from Phaser
        if (scene.sound && scene.sound.context) {
            this.audioContext = scene.sound.context;
            this.generateAllSounds();
            this.isInitialized = true;
        }
        
        // Setup event listeners
        this.setupEventListeners();
    }
    
    setupEventListeners() {
        eventBus.on(GameEvents.PLAYER_JUMP, () => this.playSFX('jump'), this);
        eventBus.on(GameEvents.PLAYER_DOUBLE_JUMP, () => this.playSFX('doubleJump'), this);
        eventBus.on(GameEvents.PLAYER_LAND, () => this.playSFX('land'), this);
        eventBus.on(GameEvents.PLAYER_DEATH, () => this.playSFX('death'), this);
        eventBus.on(GameEvents.STAR_COLLECTED, () => this.playSFX('star'), this);
        eventBus.on(GameEvents.ENEMY_KILLED, () => this.playSFX('enemyKill'), this);
        eventBus.on(GameEvents.BUTTON_PRESSED, () => this.playSFX('button'), this);
        eventBus.on(GameEvents.DOOR_OPENED, () => this.playSFX('door'), this);
        eventBus.on(GameEvents.LEVEL_COMPLETE, () => this.playSFX('win'), this);
    }
    
    /**
     * Generate all synthetic sounds
     */
    generateAllSounds() {
        if (!this.audioContext) return;
        
        this.generateJumpSound();
        this.generateDoubleJumpSound();
        this.generateLandSound();
        this.generateStarSound();
        this.generateWinSound();
        this.generateDeathSound();
        this.generateButtonSound();
        this.generateDoorSound();
        this.generateEnemyKillSound();
    }
    
    /**
     * Satisfying 'pop' jump sound
     */
    generateJumpSound() {
        const ctx = this.audioContext;
        const duration = 0.12;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq = 300 + (600 * (1 - t / duration)); // Sweep up
            const envelope = Math.exp(-t * 30);
            data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.35;
        }
        
        this.soundBuffers.set('jump', buffer);
    }
    
    /**
     * Higher pitched double jump
     */
    generateDoubleJumpSound() {
        const ctx = this.audioContext;
        const duration = 0.1;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq = 500 + (800 * (1 - t / duration));
            const envelope = Math.exp(-t * 35);
            data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.3;
        }
        
        this.soundBuffers.set('doubleJump', buffer);
    }
    
    /**
     * Soft landing thud
     */
    generateLandSound() {
        const ctx = this.audioContext;
        const duration = 0.08;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq = 60 + (30 * Math.sin(t * 80));
            const envelope = Math.exp(-t * 50);
            const noise = (Math.random() * 2 - 1) * 0.1;
            data[i] = (Math.sin(2 * Math.PI * freq * t) + noise) * envelope * 0.2;
        }
        
        this.soundBuffers.set('land', buffer);
    }
    
    /**
     * Sparkly star collection
     */
    generateStarSound() {
        const ctx = this.audioContext;
        const duration = 0.25;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq1 = 880 * Math.pow(2, t * 0.4);
            const freq2 = 1320 * Math.pow(2, t * 0.25);
            const envelope = Math.exp(-t * 10);
            data[i] = (
                Math.sin(2 * Math.PI * freq1 * t) * 0.25 +
                Math.sin(2 * Math.PI * freq2 * t) * 0.15
            ) * envelope;
        }
        
        this.soundBuffers.set('star', buffer);
    }
    
    /**
     * Triumphant win fanfare
     */
    generateWinSound() {
        const ctx = this.audioContext;
        const duration = 0.7;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        const notes = [261.63, 329.63, 392.00, 523.25]; // C4, E4, G4, C5
        const noteDuration = duration / notes.length;
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const noteIndex = Math.min(Math.floor(t / noteDuration), notes.length - 1);
            const freq = notes[noteIndex];
            const localT = t - (noteIndex * noteDuration);
            const envelope = Math.exp(-localT * 8);
            data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.2;
        }
        
        this.soundBuffers.set('win', buffer);
    }
    
    /**
     * Death/fail sound
     */
    generateDeathSound() {
        const ctx = this.audioContext;
        const duration = 0.4;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq = 400 * Math.pow(0.5, t * 3); // Descending
            const envelope = Math.exp(-t * 5);
            data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.25;
        }
        
        this.soundBuffers.set('death', buffer);
    }
    
    /**
     * Button press click
     */
    generateButtonSound() {
        const ctx = this.audioContext;
        const duration = 0.08;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq = 600;
            const envelope = Math.exp(-t * 60);
            data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.3;
        }
        
        this.soundBuffers.set('button', buffer);
    }
    
    /**
     * Door opening sound
     */
    generateDoorSound() {
        const ctx = this.audioContext;
        const duration = 0.3;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq = 200 + (t * 400);
            const envelope = Math.exp(-t * 8);
            data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.2;
        }
        
        this.soundBuffers.set('door', buffer);
    }
    
    /**
     * Enemy kill squash
     */
    generateEnemyKillSound() {
        const ctx = this.audioContext;
        const duration = 0.15;
        const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        for (let i = 0; i < buffer.length; i++) {
            const t = i / ctx.sampleRate;
            const freq = 300 * Math.pow(0.3, t * 5);
            const envelope = Math.exp(-t * 20);
            const noise = (Math.random() * 2 - 1) * 0.2;
            data[i] = (Math.sin(2 * Math.PI * freq * t) + noise * envelope) * envelope * 0.25;
        }
        
        this.soundBuffers.set('enemyKill', buffer);
    }
    
    /**
     * Play a sound effect
     */
    playSFX(soundName, volume = 1.0) {
        if (!this.sfxEnabled || !this.audioContext || !this.isInitialized) return;
        
        const buffer = this.soundBuffers.get(soundName);
        if (!buffer) return;
        
        try {
            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();
            
            source.buffer = buffer;
            gainNode.gain.value = volume * this.sfxVolume * this.masterVolume;
            
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
            
            source.start(0);
        } catch (e) {
            // Silently fail
        }
    }
    
    /**
     * Update rolling sound based on player velocity
     */
    updateRollingSound(velocityX) {
        if (!this.sfxEnabled || !this.audioContext || !this.isInitialized) return;
        
        const speed = Math.abs(velocityX);
        
        if (speed < 0.5) {
            this.stopRollingSound();
            return;
        }
        
        if (!this.rollingOscillator) {
            this.startRollingSound();
        }
        
        if (this.rollingOscillator && this.rollingGain) {
            const normalizedSpeed = Math.min(speed / 12, 1);
            const freq = 25 + (normalizedSpeed * 55);
            const vol = normalizedSpeed * 0.06 * this.sfxVolume * this.masterVolume;
            
            this.rollingOscillator.frequency.setTargetAtTime(freq, this.audioContext.currentTime, 0.1);
            this.rollingGain.gain.setTargetAtTime(vol, this.audioContext.currentTime, 0.05);
        }
    }
    
    startRollingSound() {
        if (!this.audioContext) return;
        
        try {
            this.rollingOscillator = this.audioContext.createOscillator();
            this.rollingGain = this.audioContext.createGain();
            const filter = this.audioContext.createBiquadFilter();
            
            this.rollingOscillator.type = 'sine';
            this.rollingOscillator.frequency.value = 30;
            filter.type = 'lowpass';
            filter.frequency.value = 150;
            this.rollingGain.gain.value = 0;
            
            this.rollingOscillator.connect(filter);
            filter.connect(this.rollingGain);
            this.rollingGain.connect(this.audioContext.destination);
            
            this.rollingOscillator.start();
            this.rollingSoundPlaying = true;
        } catch (e) {
            // Silently fail
        }
    }
    
    stopRollingSound() {
        if (this.rollingGain && this.audioContext) {
            this.rollingGain.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.05);
        }
    }
    
    /**
     * Play ambient lo-fi music
     */
    playAmbientMusic() {
        if (!this.musicEnabled || !this.audioContext || this.musicPlaying) return;
        
        this.stopAmbientMusic();
        
        const ctx = this.audioContext;
        
        try {
            this.musicOscillators = [];
            this.musicGain = ctx.createGain();
            this.musicGain.gain.value = 0;
            this.musicGain.connect(ctx.destination);
            
            // C major 7 chord for chill vibes
            const baseFreqs = [130.81, 164.81, 196.00, 246.94]; // C3, E3, G3, B3
            
            baseFreqs.forEach((freq, i) => {
                const osc1 = ctx.createOscillator();
                osc1.type = 'sine';
                osc1.frequency.value = freq;
                
                const osc2 = ctx.createOscillator();
                osc2.type = 'sine';
                osc2.frequency.value = freq * 1.002; // Slight detune for warmth
                
                const oscGain = ctx.createGain();
                oscGain.gain.value = 0.12 - (i * 0.015);
                
                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.value = 350;
                
                osc1.connect(oscGain);
                osc2.connect(oscGain);
                oscGain.connect(filter);
                filter.connect(this.musicGain);
                
                osc1.start();
                osc2.start();
                
                this.musicOscillators.push(osc1, osc2);
            });
            
            // Fade in
            this.musicGain.gain.setTargetAtTime(
                this.musicVolume * this.masterVolume * 0.4,
                ctx.currentTime,
                2.5
            );
            
            this.musicPlaying = true;
        } catch (e) {
            // Silently fail
        }
    }
    
    stopAmbientMusic() {
        if (this.musicOscillators.length > 0) {
            this.musicOscillators.forEach(osc => {
                try { osc.stop(); } catch (e) {}
            });
            this.musicOscillators = [];
        }
        this.musicPlaying = false;
    }
    
    // Settings
    setSFXEnabled(enabled) {
        this.sfxEnabled = enabled;
        if (!enabled) this.stopRollingSound();
    }
    
    setMusicEnabled(enabled) {
        this.musicEnabled = enabled;
        if (enabled) {
            this.playAmbientMusic();
        } else {
            this.stopAmbientMusic();
        }
    }
    
    setMasterVolume(vol) {
        this.masterVolume = Phaser.Math.Clamp(vol, 0, 1);
    }
    
    setSFXVolume(vol) {
        this.sfxVolume = Phaser.Math.Clamp(vol, 0, 1);
    }
    
    setMusicVolume(vol) {
        this.musicVolume = Phaser.Math.Clamp(vol, 0, 1);
        if (this.musicGain && this.audioContext) {
            this.musicGain.gain.setTargetAtTime(
                this.musicVolume * this.masterVolume * 0.4,
                this.audioContext.currentTime,
                0.1
            );
        }
    }
    
    destroy() {
        this.stopRollingSound();
        this.stopAmbientMusic();
        
        if (this.rollingOscillator) {
            try { this.rollingOscillator.stop(); } catch (e) {}
        }
        
        eventBus.off(GameEvents.PLAYER_JUMP, null, this);
        eventBus.off(GameEvents.PLAYER_DOUBLE_JUMP, null, this);
        eventBus.off(GameEvents.PLAYER_LAND, null, this);
        eventBus.off(GameEvents.PLAYER_DEATH, null, this);
        eventBus.off(GameEvents.STAR_COLLECTED, null, this);
        eventBus.off(GameEvents.ENEMY_KILLED, null, this);
        eventBus.off(GameEvents.BUTTON_PRESSED, null, this);
        eventBus.off(GameEvents.DOOR_OPENED, null, this);
        eventBus.off(GameEvents.LEVEL_COMPLETE, null, this);
        
        this.soundBuffers.clear();
        this.isInitialized = false;
    }
}

// Export class for instantiation
export { AudioManager };

// Singleton instance for direct import
const audioManager = new AudioManager();
export default audioManager;

